import { createSelector, createSlice, type Draft, type PayloadAction } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';
import { formatISO } from 'date-fns';

import { mifidApi } from '@/api/mifid';
import { createQuote } from '@/api/option/createQuote.ts';
import { optionApi } from '@/api/option/index.ts';
import type { ReceivedOptionQuoteReply } from '@/api/option/type.ts';
import { tradeCaptureApi } from '@/api/trade-capture/index.ts';
import { getWorkspaceMatchFulfilled } from '@/api/workspace/index.ts';
import { mapToOption } from '@/domain/FxOption/FxOption.utils.ts';
import { optionRepriceFromBlotterReducer } from '@/store/blotter/workflows/cashRepriceQuote/extraReducers.ts';
import { getRfsId } from '@/store/option/selectors/optionSelectors.ts';
import type {
  FxOption,
  LegId,
  OptionById,
  OptionProductName,
  OptionRfs,
  OptionState,
  OptionUiState,
  OptionUserPreference,
  SolvingStrike,
  StrategyPanelMode,
  StrikeSolveState,
} from '@/store/option/state.ts';
import type { ResetOptionThunkParams } from '@/store/option/thunks/thunk.ts';
import { defaultOptionUiState, emptyOption, mergeLegs } from '@/store/option/utils.ts';
import { reduceOptionOnFetchSharedWorkspace } from '@/store/sharedWorkspace/workflows/getSharedTab.workflow.ts';
import { reduceOptionStateOnSharedTabRemoved } from '@/store/sharedWorkspace/workflows/onSharedTabRemoved.workflow.ts';
import { addTile, duplicateTile, removeTile, updateTileProductType } from '@/store/tiles/slice.ts';
import type { CurrencyChoice } from '@/types/common.ts';
import { associateBy } from '@/utils/object.ts';
import {
  type IHeartbeat,
  type IQuoteAbort,
  type IQuoteTraderNotification,
  isQuoteWithdraw,
  type QuoteWithPrice,
} from '@/utils/rfs/type.ts';

const initialState: OptionState = {};

const createOptionSelector = createSelector.withTypes<OptionState>();

export const optionSlice = createSlice({
  name: 'option',
  initialState,
  reducers: {
    reset(state, action: PayloadAction<ResetOptionThunkParams & { userPreferences: OptionUserPreference }>) {
      const { tileId, userPreferences, currencyPair, newType, uiState, clientId } = action.payload;
      state[tileId] = initWithPreferences(tileId, newType, userPreferences, currencyPair, uiState, clientId);
    },

    setMaxDate(state, action: PayloadAction<{ tileId: string; maxDate: string | undefined }>) {
      const { tileId, maxDate } = action.payload;
      withOption(tileId, state, (option) => {
        option.properties.maxDate = maxDate;
      });
    },

    optimisticUpdateHedgeAmount(
      state,
      action: PayloadAction<{ tileId: string; hedgeLegId: string; hedgeAmount: number | undefined }>,
    ) {
      const { tileId, hedgeLegId, hedgeAmount } = action.payload;
      withOption(tileId, state, (option) => {
        const hedge = option.properties.hedges.find((leg) => leg.legId === hedgeLegId);
        if (isDefined(hedge)) {
          hedge.notionalAmount = hedgeAmount;
        }
      });
    },

    optimisticUpdateHedgeRate(
      state,
      action: PayloadAction<{ tileId: string; hedgeLegId: string; hedgeRate: number | undefined }>,
    ) {
      const { tileId, hedgeLegId, hedgeRate } = action.payload;
      withOption(tileId, state, (option) => {
        const hedge = option.properties.hedges.find((leg) => leg.legId === hedgeLegId);
        if (isDefined(hedge)) {
          hedge.rate = hedgeRate;
        }
      });
    },

    setSolveState(state, action: PayloadAction<{ tileId: string; legId: LegId; solveState: StrikeSolveState }>) {
      const { tileId, legId, solveState } = action.payload;
      withOption(tileId, state, (option) => {
        const leg = option.properties.legs.find((leg) => leg.legId === legId);
        if (isDefined(leg)) {
          leg.strikeSolveState = solveState;
        }
      });
    },

    setSolving(state, action: PayloadAction<{ tileId: string; legId: LegId; solvingStrike: SolvingStrike }>) {
      const { tileId, legId, solvingStrike } = action.payload;
      withOption(tileId, state, (option) => {
        const leg = option.properties.legs.find((leg) => leg.legId === legId);
        if (isDefined(leg)) {
          leg.strike = solvingStrike;
        }
      });
    },

    optionTieringUpdated(state, action: PayloadAction<{ tileId: string; tiering: string }>) {
      const tile = state[action.payload.tileId];
      if (isNotDefined(tile)) {
        return;
      }

      tile.properties.tiering = action.payload.tiering;
    },

    optionRfsQuoteAborted(state, action: PayloadAction<{ tileId: string; reply: IQuoteAbort }>) {
      const { tileId, reply } = action.payload;
      withOption(tileId, state, (option) => {
        if (reply.QuoteReject.ErrorCode === 'EM_TIMEOUT') {
          option.properties.rfs = makeExpired(option.properties.rfs);
        } else {
          option.properties.rfs = {
            status: 'ERROR',
            rfsId: getRfsId(option),
            error: {
              multipassErrorCode: reply.QuoteReject.MultipassErrorCode,
              errorCode: reply.QuoteReject.ErrorCode,
              errorMessage: reply.QuoteReject.ErrorMessage,
            },
          };
        }
      });
    },

    optionTraderNotificationReceived: (
      state,
      action: PayloadAction<{ tileId: string; message: IQuoteTraderNotification<ReceivedOptionQuoteReply> }>,
    ) => {
      withOption(action.payload.tileId, state, (option) => {
        const traderId = action.payload.message.OptionQuoteReply?.TraderId ?? undefined;

        if (option.properties.rfs.status === 'PRICING' || option.properties.rfs.status === 'AWAITING') {
          option.properties.rfs = {
            ...option.properties.rfs,
            traderId,
          };
        }
      });
    },

    optionRfsQuoteReceived(
      state,
      action: PayloadAction<{ tileId: string; message: QuoteWithPrice<ReceivedOptionQuoteReply> }>,
    ) {
      const { tileId, message } = action.payload;

      withOption(tileId, state, (option) => {
        const quote = createQuote(message);
        if (option.properties.rfs.status === 'PRICING' || option.properties.rfs.status === 'AWAITING') {
          const initialRfsWindow =
            option.properties.rfs.status === 'PRICING' ? option.properties.rfs.initialRfsWindow : quote.rfsWindow;
          option.properties.rfs = {
            status: 'PRICING',
            quote,
            rfsId: option.properties.rfs.rfsId,
            quoteType: isQuoteWithdraw(message) ? 'Indicative' : quote.quoteType,
            traderId: option.properties.rfs.traderId,
            initialRfsWindow,
          };
        }
      });
    },

    optionRfsHeartbeat(_state, _action: PayloadAction<{ tileId: string; heartbeat: IHeartbeat }>) {},

    optionClientIdUpdated(state, action: PayloadAction<{ tileId: string; clientId: number }>) {
      const tile = state[action.payload.tileId];
      if (isNotDefined(tile) || tile.clientId === action.payload.clientId) {
        return;
      }

      tile.properties.tiering = undefined;
    },

    changeClient(state, action: PayloadAction<{ tileId: string; clientId: number | undefined }>) {
      const { tileId, clientId } = action.payload;
      withOption(tileId, state, (tile) => {
        if (tile.clientId === clientId) {
          return;
        }

        tile.clientId = clientId;
        tile.properties.tiering = undefined;
      });
    },

    changeLegProductName(
      state,
      action: PayloadAction<{ tileId: string; legId: LegId; legProductName: 'Vanilla' | 'Trigger' | 'Binary' }>,
    ) {
      const { tileId, legId, legProductName } = action.payload;
      withOption(tileId, state, (tile) => {
        const leg = tile.properties.legs.find((leg) => leg.legId === legId);
        if (isDefined(leg)) {
          leg.legProductName = legProductName;
        }
      });
    },

    setLegCurrencies(
      state,
      action: PayloadAction<{ tileId: string; legCurrencies: { legId: LegId; currency: CurrencyChoice }[] }>,
    ) {
      const { tileId, legCurrencies } = action.payload;
      withOption(tileId, state, (tile) => {
        const ccyChangesByLegId = associateBy(legCurrencies, (legCcy) => legCcy.legId);
        for (const leg of tile.properties.legs) {
          const ccyChanges = ccyChangesByLegId[leg.legId];
          if (isDefined(ccyChanges)) {
            leg.notionalCurrencyIndex = ccyChanges.currency;
          }
        }
      });
    },

    setStrategyPanelMode(state, action: PayloadAction<{ tileId: string; mode: StrategyPanelMode }>) {
      const { tileId, mode } = action.payload;
      withOption(tileId, state, (tile) => {
        tile.properties.uiState.strategyPanelMode = mode;
      });
    },

    setLegsNotionalLinkedCurrency(state, action: PayloadAction<{ tileId: string; linked: 'linked' | 'unlinked' }>) {
      const { tileId, linked } = action.payload;
      withOption(tileId, state, (tile) => {
        tile.properties.uiState.legsNotionalCurrenciesLinkState = linked;
      });
    },

    toggleGroupedByExpiry(state, action: PayloadAction<{ tileId: string }>) {
      const { tileId } = action.payload;
      withOption(tileId, state, (tile) => {
        tile.properties.uiState.groupLegsByExpiry = !tile.properties.uiState.groupLegsByExpiry;
      });
    },

    // EXECUTION BOILERPLATE
    executionPending: (state, action: PayloadAction<{ tileId: string; executionId: string }>) => {
      const { tileId } = action.payload;
      withOption(tileId, state, (tile) => {
        tile.execution = {
          status: 'PENDING',
          executionId: action.payload.executionId,
        };
        tile.properties.rfs = makeExpired(tile.properties.rfs);
      });
    },
    executionSuccess: (state, action: PayloadAction<{ tileId: string }>) => {
      const { tileId } = action.payload;
      withOption(tileId, state, (tile) => {
        if (isDefined(tile.execution)) {
          tile.execution.status = 'SUCCESS';
        }
      });
    },
    executionRejected: (state, action: PayloadAction<{ tileId: string }>) => {
      const { tileId } = action.payload;
      withOption(tileId, state, (tile) => {
        if (isDefined(tile.execution)) {
          tile.execution.status = 'REJECTED';
        }
      });
    },
    executionReset: (state, action: PayloadAction<{ tileId: string }>) => {
      const { tileId } = action.payload;
      withOption(tileId, state, (tile) => {
        tile.execution = undefined;
      });
    },
  },

  extraReducers: (builder) => {
    reduceOptionStateOnSharedTabRemoved(builder);

    builder.addCase(
      updateTileProductType,
      (state, { payload: { tileId, productType, userPreferences, currencyPair, overriddenClientId } }) => {
        if (isDefined(state[tileId]) && productType !== 'Option') {
          delete state[tileId];
        }
        if (productType === 'Option') {
          state[tileId] = initWithPreferences(
            tileId,
            'Vanilla',
            userPreferences,
            currencyPair,
            undefined,
            overriddenClientId,
          );
        }
      },
    );

    builder.addCase(addTile, (state, { payload }) => {
      const {
        tile: { tileId, productType },
        userPreferences,
      } = payload;
      if (isDefined(state?.[tileId])) {
        return state;
      }

      if (productType === 'Option') {
        state[tileId] = initWithPreferences(tileId, 'Vanilla', userPreferences);
      }
    });
    builder.addCase(removeTile, (state, { payload: tileId }) => {
      if (isNotDefined(state[tileId])) {
        return state;
      }

      delete state[tileId];
    });

    builder.addCase(duplicateTile, (state, action) => {
      const { sourceTileId, newTileId } = action.payload;

      const sourceTile = state[sourceTileId];

      if (isNotDefined(sourceTile)) {
        return state;
      }
      if (isDefined(state[newTileId])) {
        return state;
      }

      state[newTileId] = emptyOption(newTileId, sourceTile.type, sourceTile.properties, sourceTile.clientId);
    });

    // reprice from blotter
    optionRepriceFromBlotterReducer(builder);

    builder.addMatcher(
      tradeCaptureApi.endpoints.checkOption.matchPending,
      function onTradeCaptureUpdated(state, action) {
        const { tileId } = action.meta.arg.originalArgs;
        withOption(tileId, state, (option) => {
          option.tradeCapture.status = 'CHECKING';
        });
      },
    );

    builder.addMatcher(
      tradeCaptureApi.endpoints.checkOption.matchRejected,
      function onTradeCaptureError(state, action) {
        const { tileId } = action.meta.arg.originalArgs;
        withOption(tileId, state, (option) => {
          option.tradeCapture = {
            status: 'ERROR',
            // TODO: this can be a TIMEOUT, but this can also be another type of error
            error: 'TIMEOUT',
            // TODO: are those fields relevant?
            sessionId: option.tradeCapture.sessionId,
            version: option.tradeCapture.version,
          };
        });
      },
    );

    builder.addMatcher(
      tradeCaptureApi.endpoints.checkOption.matchFulfilled,
      function onTradeCaptureUpdated(state, action) {
        const { tileId, productName } = action.meta.arg.originalArgs;

        const currentTile = state[tileId];

        if (isNotDefined(currentTile) || isDefined(currentTile.execution)) {
          // already executed
          return;
        }

        const tcResponse = action.payload;
        currentTile.tradeCapture = tcResponse.tradeCapture;

        if (tcResponse.type === 'error') {
          return;
        }

        // clear tiering to let esp listeners update it
        // TODO: not very obvious
        if (currentTile.properties.currencyPair !== tcResponse.properties.currencyPair) {
          currentTile.properties.tiering = undefined;
          currentTile.properties.isEspDefaultTiering = undefined;
        }

        const legs = mergeLegs(currentTile.properties.legs, tcResponse.properties.legs ?? []);
        const hedges = mergeLegs(currentTile.properties.hedges, tcResponse.properties.hedges ?? []);

        currentTile.type = productName;
        currentTile.properties = {
          ...currentTile.properties,
          ...tcResponse.properties,
        };
        currentTile.properties.legs = legs;
        currentTile.properties.hedges = hedges;
      },
    );

    builder.addMatcher(tradeCaptureApi.endpoints.createStrip.matchFulfilled, function onStripReceived(state, action) {
      const { tileId } = action.meta.arg.originalArgs;

      const tcResponse = action.payload;
      withOption(tileId, state, (option) => {
        if (tcResponse.type === 'error') {
          return;
        }

        const legs = mergeLegs(option.properties.legs, tcResponse.properties.legs ?? []);
        option.tradeCapture = tcResponse.tradeCapture;
        option.properties.legs = legs;
      });
    });

    // REQUEST
    builder.addMatcher(optionApi.endpoints.requestOptionRfs.matchPending, function beforeRfsRequest(state, action) {
      const tileId = action.meta.arg.originalArgs.tileId;
      withOption(tileId, state, (option) => {
        const rfsId = action.meta.arg.originalArgs.rfsId;
        option.properties.rfs = {
          status: 'AWAITING',
          rfsId,
          quoteType: action.meta.arg.originalArgs.body.forceTradable ? 'Tradeable' : 'Indicative',
        };
        option.properties.mifid.rfsDate = new Date().toISOString();
      });
    });

    // CANCEL
    builder.addMatcher(optionApi.endpoints.cancelOptionRfs.matchPending, function beforeCancelRfs(state, action) {
      const { tileId, removeQuote } = action.meta.arg.originalArgs;
      withOption(tileId, state, (option) => {
        if (removeQuote) {
          option.properties.rfs = {
            status: 'CANCELLED',
          };
        } else {
          option.properties.rfs = makeExpired(option.properties.rfs);
        }
      });
    });
    // UPDATE
    builder.addMatcher(optionApi.endpoints.updateOptionRfs.matchFulfilled, function afterUpdateRfs(state, action) {
      const tileId = action.meta.arg.originalArgs.tileId;
      withOption(tileId, state, (option) => {
        option.properties.rfsVersion = action.meta.arg.originalArgs.request.version;
      });
    });

    builder.addMatcher(getWorkspaceMatchFulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }
      // clear all products created before workspace has loaded
      Object.keys(state).forEach((id) => {
        delete state[id];
      });
      action.payload.products.option.forEach((optionProduct) => {
        state[optionProduct.tileId] = mapToOption(optionProduct);
      });
    });
    reduceOptionOnFetchSharedWorkspace(builder);

    builder.addMatcher(mifidApi.endpoints.recordPrice.matchFulfilled, (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;
      withOption(tileId, state, (option) => {
        option.properties.mifid.records.push({ date: formatISO(action.meta.arg.originalArgs.date) });
      });
    });
    builder.addMatcher(mifidApi.endpoints.recordPrice.matchRejected, (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;
      withOption(tileId, state, (option) => {
        option.properties.mifid.records.push({ date: formatISO(action.meta.arg.originalArgs.date), isError: true });
      });
    });
  },
  selectors: {
    getAllOptionProducts: createOptionSelector([(state) => state], (state) =>
      Object.entries(state).map(([tileId, option]) => ({ tileId, ...option })),
    ),
    getOptionTile: (state, tileId: string): FxOption | undefined => state[tileId],
  },
});

function makeExpired(rfs: OptionRfs): OptionRfs {
  if (rfs.status === 'EXPIRED' || rfs.status === 'PRICING') {
    return {
      status: 'EXPIRED',
      quote: rfs.quote,
    };
  }
  return { status: 'CANCELLED' };
}

function initWithPreferences(
  tileId: string,
  product: OptionProductName,
  userPreferences: OptionUserPreference,
  currencyPair?: string,
  uiState?: OptionUiState,
  overriddenClientId?: number,
) {
  const {
    // Expand Greeks and market data by default
    optionExpandGreekAndMkt,
    // Group legs by expiry date by default
    optionStrategyGroupLegs,
  } = userPreferences;

  const newUiState = {
    ...defaultOptionUiState,
    groupLegsByExpiry: optionStrategyGroupLegs,
    expandGreekAndMarketPanels: optionExpandGreekAndMkt,
    ...uiState,
  };

  return emptyOption(
    tileId,
    product,
    {
      currencyPair,
      uiState: newUiState,
    },
    overriddenClientId,
  );
}

function withOption(tileId: string, state: OptionById, action: (option: Draft<FxOption>) => void) {
  const currentOption = state[tileId];
  if (isDefined(currentOption)) {
    action(currentOption);
  }
}
